import { BaseService } from '@common/http-layer/service/base.service'
import { generateDRMImageUrl } from '../utils/image.utils'
import type {
  AssetsDrmLicenseResponse,
  CloneAssetsDrmLicensePayload,
  CloneAssetsDrmLicenseResponse,
} from '../types/assets-drm.types'

const shouldNormalizeIncompleteImageUrl = (asset: AssetsDrmLicenseResponse): boolean => {
  if (asset.assetType !== 'image' || !asset.licenseId) return false
  const status = asset.status?.toLowerCase()
  return status !== 'completed'
}

const extractSourceFromAssetUrl = (assetUrl?: string): string | undefined => {
  if (!assetUrl) return undefined
  // Supports both absolute and relative URLs; captures first path segment as source.
  const sourceMatch = assetUrl.match(/^(?:https?:\/\/[^/]+)?\/?([^/?#]+)/i)
  return sourceMatch?.[1]
}

export const normalizeAssetsDrmLicenseResponse = (asset: AssetsDrmLicenseResponse): AssetsDrmLicenseResponse => {
  if (!shouldNormalizeIncompleteImageUrl(asset)) {
    return asset
  }

  const source = extractSourceFromAssetUrl(asset.url)

  return {
    ...asset,
    // API can return a non-renderable URL until status is completed (e.g. remaining, processing).
    // Use deterministic licenseId-based DRM image URL, same path shape used by SmartImage/OptimisedImage.
    url: generateDRMImageUrl({ licenseId: asset.licenseId, source }),
  }
}

export class AssetsDrmService extends BaseService {
  constructor() {
    super({
      baseURL: 'ASSETS_DRM_API_URL',
      basePath: '/assets-license',
    })
  }

  async getAssetInfo(licenseId: string): Promise<AssetsDrmLicenseResponse> {
    if (!licenseId) throw new Error('licenseId is required')

    const { data } = await this.get<AssetsDrmLicenseResponse>(`/${licenseId}`)
    return normalizeAssetsDrmLicenseResponse(data)
  }

  async getAssetsInfoBulk(licenseIds: string[]): Promise<AssetsDrmLicenseResponse[]> {
    if (!licenseIds || licenseIds.length === 0) return []

    const results = await Promise.all(
      licenseIds.map(licenseId =>
        this.getAssetInfo(licenseId).catch(err => {
          console.warn(`Failed to fetch asset info for ${licenseId}:`, err)
          return null
        })
      )
    )

    // Filter out failed requests
    return results.filter((result): result is AssetsDrmLicenseResponse => result !== null)
  }

  async cloneLicense(payload: CloneAssetsDrmLicensePayload): Promise<CloneAssetsDrmLicenseResponse> {
    const { data } = await this.post<CloneAssetsDrmLicenseResponse>('/snapshots/clone-license', payload)
    return data
  }
}
